/**
 * Utility to identify JSON parse errors caused by HTML responses
 * (e.g., when an API proxy or server returns an HTML error page like 502/503/401
 * starting with '<' instead of valid JSON).
 */

export const isHtmlPayload = (text: string): boolean => {
  if (!text || typeof text !== 'string') return false;
  const trimmed = text.trim();
  return (
    trimmed.startsWith('<') ||
    trimmed.startsWith('<!DOCTYPE') ||
    trimmed.startsWith('<!doctype') ||
    trimmed.startsWith('<html') ||
    trimmed.startsWith('<HTML')
  );
};

export const isJsonParseError = (error: unknown): boolean => {
  if (!error) return false;

  if (typeof error === 'string') {
    return (
      /json[ .]parse error/i.test(error) ||
      /unrecognized token.*[<]/i.test(error) ||
      /unexpected token.*[<]/i.test(error) ||
      /unexpected character at line \d+.*[<]/i.test(error) ||
      (/is not valid json/i.test(error) && error.includes('<'))
    );
  }

  if (typeof error === 'object') {
    const err = error as {
      name?: string;
      message?: string;
      networkError?: unknown;
      bodyText?: string;
      error?: unknown;
    };

    if (err.name === 'ServerParseError') return true;

    if (typeof err.message === 'string') {
      const msg = err.message;
      if (
        /json[ .]parse error/i.test(msg) ||
        /unrecognized token.*[<]/i.test(msg) ||
        /unexpected token.*[<]/i.test(msg) ||
        /unexpected character at line \d+.*[<]/i.test(msg) ||
        (/is not valid json/i.test(msg) && msg.includes('<'))
      ) {
        return true;
      }
    }

    if (err.bodyText && isHtmlPayload(err.bodyText)) {
      return true;
    }

    if (err.networkError && isJsonParseError(err.networkError)) {
      return true;
    }

    if (err.error && isJsonParseError(err.error)) {
      return true;
    }
  }

  return false;
};

export const formatFriendlyNetworkErrorMessage = (status?: number): string => {
  switch (status) {
    case 401:
    case 403:
      return 'Votre session a expiré. Veuillez vous reconnecter.';
    case 502:
    case 503:
    case 504:
      return 'Le service est temporairement indisponible. Veuillez réessayer dans quelques instants.';
    case 500:
      return 'Une erreur interne du serveur est survenue. Veuillez réessayer ultérieurement.';
    case 404:
      return 'Le service demandé est introuvable.';
    default:
      return 'Impossible de joindre le serveur. Veuillez vérifier votre connexion et réessayer.';
  }
};

export default isJsonParseError;
